import { create } from "zustand";
import { router } from "../data/Services/ordersRouter";

// Per-symbol OTO defaults (used when no explicit value has been set yet)
const DEFAULT_OTO_ENABLED = false;
const DEFAULT_OTO_LOSS_TICKS = 10;
const DEFAULT_OTO_PROFIT_TICKS = 10;

// Clamp a tick-distance input to a positive integer
const sanitizeTicks = (value) => Math.max(1, Math.round(Number(value) || 0));

// Zustand store for order defaults - quantity + OTO config per symbol (shared across panels)
export const useOrderDefaultsStore = create((set, get) => ({
  // Quantity per symbol (shared across all panels showing the same symbol)
  quantities: {},
  // Ordem OTO armed flag per symbol
  otoEnabled: {},
  // OTO stop-loss (PERDA) distance in ticks per symbol
  otoLossTicks: {},
  // OTO take-profit (GANHO) distance in ticks per symbol
  otoProfitTicks: {},
  // Symbol per chartId (to track which symbol each chart shows)
  symbols: {},
  // Currently active chartId
  activeChartId: null,

  // Set active chart and initialize quantity + OTO defaults for its symbol if needed
  setActiveChart: (chartId, symbol) => {
    if (!chartId) return;

    const state = get();
    const isNewSymbol = symbol && !(symbol in state.quantities);

    // Initialize quantity + OTO config only if this symbol hasn't been seen before
    if (isNewSymbol) {
      let minimumLot = 1;
      if (router?.adjustStandardLot) {
        minimumLot = router.adjustStandardLot(symbol, 1);
      }
      set((s) => ({
        activeChartId: chartId,
        quantities: { ...s.quantities, [symbol]: minimumLot },
        otoEnabled: { ...s.otoEnabled, [symbol]: DEFAULT_OTO_ENABLED },
        otoLossTicks: { ...s.otoLossTicks, [symbol]: DEFAULT_OTO_LOSS_TICKS },
        otoProfitTicks: {
          ...s.otoProfitTicks,
          [symbol]: DEFAULT_OTO_PROFIT_TICKS,
        },
        symbols: { ...s.symbols, [chartId]: symbol },
      }));
    } else {
      // Just switch active chart, keep existing quantity/OTO config for this symbol
      set((s) => ({
        activeChartId: chartId,
        symbols: { ...s.symbols, [chartId]: symbol },
      }));
    }
  },

  // Set quantity for a symbol (defaults to the active chart's symbol).
  // Pass an explicit symbol to target a non-active chart's instrument.
  setQuantity: (newQuantity, symbolArg) => {
    const { activeChartId, symbols } = get();
    const symbol = symbolArg || symbols[activeChartId];
    if (!symbol) return;

    let adjustedQuantity = newQuantity;
    if (router?.adjustStandardLot) {
      adjustedQuantity = router.adjustStandardLot(symbol, newQuantity);
    }

    set((s) => ({
      quantities: { ...s.quantities, [symbol]: Math.max(adjustedQuantity, 1) },
    }));
  },

  // Get quantity for active chart's symbol
  getQuantity: () => {
    const { activeChartId, quantities, symbols } = get();
    if (!activeChartId) return 1;
    const symbol = symbols[activeChartId];
    if (!symbol) return 1;
    return quantities[symbol] ?? 1;
  },

  // Get adjusted quantity for a symbol (defaults to the active chart's symbol).
  // Pass an explicit symbol to size an order for a non-active chart's instrument.
  getAdjustedQuantity: (symbolArg) => {
    const { activeChartId, quantities, symbols } = get();
    const symbol = symbolArg || symbols[activeChartId];
    if (!symbol) return 1;

    const quantity = quantities[symbol] ?? 1;

    if (router?.adjustStandardLot) {
      return router.adjustStandardLot(symbol, quantity);
    }
    return quantity;
  },

  // --- Ordem OTO config (per symbol) ---

  // Set the OTO armed flag (defaults to the active chart's symbol)
  setOtoEnabled: (enabled, symbolArg) => {
    const { activeChartId, symbols } = get();
    const symbol = symbolArg || symbols[activeChartId];
    if (!symbol) return;
    set((s) => ({ otoEnabled: { ...s.otoEnabled, [symbol]: !!enabled } }));
  },

  // Set the OTO stop-loss (PERDA) tick distance (defaults to the active chart's symbol)
  setOtoLossTicks: (ticks, symbolArg) => {
    const { activeChartId, symbols } = get();
    const symbol = symbolArg || symbols[activeChartId];
    if (!symbol) return;
    set((s) => ({
      otoLossTicks: { ...s.otoLossTicks, [symbol]: sanitizeTicks(ticks) },
    }));
  },

  // Set the OTO take-profit (GANHO) tick distance (defaults to the active chart's symbol)
  setOtoProfitTicks: (ticks, symbolArg) => {
    const { activeChartId, symbols } = get();
    const symbol = symbolArg || symbols[activeChartId];
    if (!symbol) return;
    set((s) => ({
      otoProfitTicks: { ...s.otoProfitTicks, [symbol]: sanitizeTicks(ticks) },
    }));
  },

  // Get the OTO armed flag for a symbol (defaults to the active chart's symbol)
  getOtoEnabled: (symbolArg) => {
    const { activeChartId, symbols, otoEnabled } = get();
    const symbol = symbolArg || symbols[activeChartId];
    if (!symbol) return DEFAULT_OTO_ENABLED;
    return otoEnabled[symbol] ?? DEFAULT_OTO_ENABLED;
  },

  // Get the OTO stop-loss tick distance for a symbol (defaults to the active chart's symbol)
  getOtoLossTicks: (symbolArg) => {
    const { activeChartId, symbols, otoLossTicks } = get();
    const symbol = symbolArg || symbols[activeChartId];
    if (!symbol) return DEFAULT_OTO_LOSS_TICKS;
    return otoLossTicks[symbol] ?? DEFAULT_OTO_LOSS_TICKS;
  },

  // Get the OTO take-profit tick distance for a symbol (defaults to the active chart's symbol)
  getOtoProfitTicks: (symbolArg) => {
    const { activeChartId, symbols, otoProfitTicks } = get();
    const symbol = symbolArg || symbols[activeChartId];
    if (!symbol) return DEFAULT_OTO_PROFIT_TICKS;
    return otoProfitTicks[symbol] ?? DEFAULT_OTO_PROFIT_TICKS;
  },

  // Get the full OTO config for a symbol (defaults to the active chart's symbol).
  // Used by non-React order paths via useOrderDefaultsStore.getState().
  getOtoConfig: (symbolArg) => {
    const g = get();
    const symbol = symbolArg || g.symbols[g.activeChartId];
    return {
      enabled: g.getOtoEnabled(symbol),
      lossTicks: g.getOtoLossTicks(symbol),
      profitTicks: g.getOtoProfitTicks(symbol),
    };
  },

  // Backward compatibility: get symbol for active chart
  getSymbol: () => {
    const { activeChartId, symbols } = get();
    return symbols[activeChartId] || "";
  },

  // --- Desktop persistence ---

  // Per-symbol config that belongs to the desktop snapshot.
  // Ephemeral runtime state (symbols, activeChartId) is intentionally excluded.
  serialize: () => {
    const { quantities, otoEnabled, otoLossTicks, otoProfitTicks } = get();
    return { quantities, otoEnabled, otoLossTicks, otoProfitTicks };
  },

  // Replace per-symbol config from a desktop snapshot (used on desktop restore).
  hydrate: (data) => {
    if (!data || typeof data !== "object") return;
    set({
      quantities: data.quantities || {},
      otoEnabled: data.otoEnabled || {},
      otoLossTicks: data.otoLossTicks || {},
      otoProfitTicks: data.otoProfitTicks || {},
    });
  },

  // Clear all per-symbol config (used before restoring a desktop)
  reset: () =>
    set({
      quantities: {},
      otoEnabled: {},
      otoLossTicks: {},
      otoProfitTicks: {},
    }),
}));

// Hook to provide order defaults per symbol. Without an explicit symbol it uses
// the active chart for backward compatibility.
export const useOrderDefaults = (symbolArg) => {
  const store = useOrderDefaultsStore();
  const symbol = symbolArg || store.getSymbol();

  return {
    quantity: store.getAdjustedQuantity(symbol),
    setQuantity: (newQuantity, targetSymbol) =>
      store.setQuantity(newQuantity, targetSymbol || symbol),
    setActiveChart: store.setActiveChart,
    getAdjustedQuantity: store.getAdjustedQuantity,
    // Ordem OTO config for the selected symbol
    otoEnabled: store.getOtoEnabled(symbol),
    otoLossTicks: store.getOtoLossTicks(symbol),
    otoProfitTicks: store.getOtoProfitTicks(symbol),
    setOtoEnabled: (enabled, targetSymbol) =>
      store.setOtoEnabled(enabled, targetSymbol || symbol),
    setOtoLossTicks: (ticks, targetSymbol) =>
      store.setOtoLossTicks(ticks, targetSymbol || symbol),
    setOtoProfitTicks: (ticks, targetSymbol) =>
      store.setOtoProfitTicks(ticks, targetSymbol || symbol),
    // Backward compatibility
    symbol,
    setSymbol: (nextSymbol) => {
      const chartId = store.activeChartId;
      if (chartId) store.setActiveChart(chartId, nextSymbol);
    },
  };
};
